import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { useToast } from "@/hooks/use-toast";
import {
  geocodeAddress,
  getCurrentPosition,
  normalizeCityName,
  type GeocodeResult,
} from "@/lib/geolocation";
import { apiRequest } from "@/lib/queryClient";
import {
  PlacesAutocompleteSchema,
  PlacesDetailsSchema,
} from "@shared/api-schemas";
import { getJson } from "@shared/http";
import { StateCode } from "@shared/regions";
import {
  australianStates,
  getNearestSuburb,
  getStateFromCoordinates,
  getSuburbsByState,
  suburbsByState,
  type SuburbInfo,
} from "@shared/suburbs";
import { ChevronDown, MapPin, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface LocationSelectorProps {
  variant?: "button" | "compact";
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  mobile?: boolean; // 모바일에서 "지역"만 표시
}

export function LocationSelector({
  variant = "button",
  className = "",
  open: controlledOpen,
  onOpenChange,
  mobile = false,
}: LocationSelectorProps) {
  const { userProfile } = useAuth();
  const {
    selectedRegion,
    setSelectedRegion,
    selectedSuburb,
    setSelectedSuburb,
  } = useRegion();
  const { toast } = useToast();
  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled or uncontrolled mode
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [tempState, setTempState] = useState(selectedRegion);
  const [tempSuburb, setTempSuburb] = useState(selectedSuburb || "");
  const [detecting, setDetecting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    setTempState(selectedRegion);
    setTempSuburb(selectedSuburb || "");
    if (open) {
      setSearchTerm("");
    }
  }, [selectedRegion, selectedSuburb, open]);

  const currentStateInfo = australianStates.find(
    (s) => s.id === selectedRegion
  );
  const currentSuburbInfo = selectedSuburb
    ? getSuburbsByState(selectedRegion).find((s) => s.id === selectedSuburb)
    : null;

  // 현재 위치로 자동 설정
  const handleDetectLocation = async () => {
    if (!navigator.geolocation) {
      toast({
        title: "위치 서비스 사용 불가",
        description: "브라우저가 위치 서비스를 지원하지 않습니다.",
        variant: "destructive",
      });
      return;
    }

    setDetecting(true);
    try {
      const position = await getCurrentPosition();
      const { latitude, longitude } = position.coords;

      const detectedState = getStateFromCoordinates(
        latitude,
        longitude
      ) as StateCode;
      setTempState(detectedState);

      const nearestSuburb = getNearestSuburb(
        latitude,
        longitude,
        detectedState
      );
      if (nearestSuburb) {
        setTempSuburb(nearestSuburb.id);
        toast({
          title: "위치를 찾았습니다",
          description: `${nearestSuburb.nameKo || nearestSuburb.name}, ${
            australianStates.find((s) => s.id === detectedState)?.nameKo
          }`,
        });
      } else {
        setTempSuburb("");
        toast({
          title: "주를 찾았습니다",
          description: `${
            australianStates.find((s) => s.id === detectedState)?.nameKo
          }`,
        });
      }
    } catch (error: any) {
      console.error("Location detection error:", error);
      toast({
        title: "위치 감지 실패",
        description: error?.message || "현재 위치를 찾을 수 없습니다.",
        variant: "destructive",
      });
    } finally {
      setDetecting(false);
    }
  };

  // 주요도시 선택
  const handleMajorCitySelect = (stateId: StateCode, suburbId?: string) => {
    setTempState(stateId);
    if (suburbId) {
      setTempSuburb(suburbId);
    } else {
      setTempSuburb("");
    }
    toast({
      title: "지역이 선택되었습니다",
      description: suburbId
        ? `${
            getSuburbsByState(stateId).find((s) => s.id === suburbId)?.nameKo ||
            suburbId
          }, ${australianStates.find((s) => s.id === stateId)?.nameKo}`
        : `${australianStates.find((s) => s.id === stateId)?.nameKo}`,
    });
  };

  // 우편번호/도시명 검색
  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term || term.length < 2) {
      toast({
        title: "검색어를 입력해주세요",
        description: "우편번호 또는 도시명을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }

    setSearching(true);
    try {
      const normalizedTerm = normalizeCityName(term);
      const searchQuery = /^\d+$/.test(term)
        ? `${term} Australia`
        : `${normalizedTerm} Australia`;

      const geocodeResult = await geocodeAddress(searchQuery);

      if (geocodeResult) {
        const detectedState =
          (geocodeResult.state as StateCode) ||
          (getStateFromCoordinates(
            geocodeResult.lat,
            geocodeResult.lng
          ) as StateCode);
        setTempState(detectedState);

        if (geocodeResult.city) {
          // 도시명이 있으면 가장 가까운 동네 찾기
          const nearestSuburb = getNearestSuburb(
            geocodeResult.lat,
            geocodeResult.lng,
            detectedState
          );
          if (nearestSuburb) {
            setTempSuburb(nearestSuburb.id);
            toast({
              title: "지역을 찾았습니다",
              description: `${nearestSuburb.nameKo || nearestSuburb.name}, ${
                australianStates.find((s) => s.id === detectedState)?.nameKo
              }`,
            });
          } else {
            // 동네를 찾지 못하면 도시명을 커스텀 지역으로 사용
            setTempSuburb(geocodeResult.city);
            toast({
              title: "지역을 찾았습니다",
              description: `${geocodeResult.city}, ${
                australianStates.find((s) => s.id === detectedState)?.nameKo
              }`,
            });
          }
        } else {
          // 도시명이 없으면 가장 가까운 동네 찾기
          const nearestSuburb = getNearestSuburb(
            geocodeResult.lat,
            geocodeResult.lng,
            detectedState
          );
          if (nearestSuburb) {
            setTempSuburb(nearestSuburb.id);
            toast({
              title: "지역을 찾았습니다",
              description: `${nearestSuburb.nameKo || nearestSuburb.name}, ${
                australianStates.find((s) => s.id === detectedState)?.nameKo
              }`,
            });
          } else {
            setTempSuburb("");
            toast({
              title: "주를 찾았습니다",
              description: `${
                australianStates.find((s) => s.id === detectedState)?.nameKo
              }`,
            });
          }
        }
      } else {
        toast({
          title: "검색 실패",
          description: "지역을 찾을 수 없습니다. 다른 검색어를 시도해주세요.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      toast({
        title: "검색 실패",
        description: "지역을 찾을 수 없습니다.",
        variant: "destructive",
      });
    } finally {
      setSearching(false);
    }
  };

  // 저장
  const handleSave = async () => {
    setSelectedRegion(tempState);
    setSelectedSuburb(tempSuburb || undefined);

    // Save to the real D1 account record if logged in (see RegionContext.tsx
    // for why this no longer goes through userService.ts's Firestore
    // updateUserProfile - that write was never read back by anything).
    if (userProfile) {
      try {
        await apiRequest("POST", "/api/me/location", {
          regionCode: tempState,
          suburb: tempSuburb || null,
        });
      } catch (error) {
        console.error("Failed to save location:", error);
      }
    }

    toast({
      title: "지역이 설정되었습니다",
      description: tempSuburb
        ? `${
            getSuburbsByState(tempState).find((s) => s.id === tempSuburb)
              ?.nameKo || tempSuburb
          }, ${australianStates.find((s) => s.id === tempState)?.nameKo}`
        : `${australianStates.find((s) => s.id === tempState)?.nameKo}`,
    });

    setOpen(false);
  };

  if (variant === "compact") {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          className={`gap-2 ${className}`}
          data-testid="button-location-compact"
        >
          <MapPin className="h-4 w-4" />
          <span className="font-medium">
            {mobile
              ? "지역"
              : currentSuburbInfo?.nameKo || currentStateInfo?.nameKo}
          </span>
          {!mobile && <ChevronDown className="h-3 w-3" />}
        </Button>
        <LocationDialog
          open={open}
          onOpenChange={setOpen}
          tempState={tempState}
          setTempState={setTempState}
          tempSuburb={tempSuburb}
          setTempSuburb={setTempSuburb}
          detecting={detecting}
          onDetect={handleDetectLocation}
          onSave={handleSave}
          onMajorCitySelect={handleMajorCitySelect}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searching={searching}
          onSearch={handleSearch}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className={`gap-2 ${className}`}
        data-testid="button-location-selector"
      >
        <MapPin className="h-4 w-4" />
        <span>
          {currentSuburbInfo
            ? `${currentSuburbInfo.nameKo || currentSuburbInfo.name}`
            : currentStateInfo?.nameKo || selectedRegion}
        </span>
        <ChevronDown className="h-4 w-4" />
      </Button>
      <LocationDialog
        open={open}
        onOpenChange={setOpen}
        tempState={tempState}
        setTempState={setTempState}
        tempSuburb={tempSuburb}
        setTempSuburb={setTempSuburb}
        detecting={detecting}
        onDetect={handleDetectLocation}
        onSave={handleSave}
        onMajorCitySelect={handleMajorCitySelect}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searching={searching}
        onSearch={handleSearch}
      />
    </>
  );
}

interface LocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tempState: StateCode;
  setTempState: (state: StateCode) => void;
  tempSuburb: string;
  setTempSuburb: (suburb: string) => void;
  detecting: boolean;
  onDetect: () => void;
  onSave: () => void;
  onMajorCitySelect: (stateId: StateCode, suburbId?: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  searching: boolean;
  onSearch: () => void;
}

function LocationDialog({
  open,
  onOpenChange,
  tempState,
  setTempState,
  tempSuburb,
  setTempSuburb,
  detecting,
  onDetect,
  onSave,
  onMajorCitySelect,
  searchTerm,
  setSearchTerm,
  searching,
  onSearch,
}: LocationDialogProps) {
  const selectedStateInfo = australianStates.find((s) => s.id === tempState);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { toast } = useToast();

  // 자동완성 검색 (debounced) - 로컬 검색 우선, Google Places는 백그라운드
  const handleAutocompleteSearch = useCallback(async (term: string) => {
    if (!term || term.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // 로컬 검색 결과가 있으면 즉시 표시 (빠른 응답)
    // Google Places API는 백그라운드에서 처리하고 결과가 오면 추가

    try {
      // 우편번호 검색 (숫자만 입력한 경우) - 최소 4자리만 검색
      if (/^\d+$/.test(term.trim()) && term.trim().length >= 4) {
        console.log("🔍 Postcode search:", term.trim());

        // 타임아웃 설정 (10초로 증가 - 여러 검색어 시도 시간 고려)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout")), 10000)
        );

        // 여러 주와 함께 검색 시도 (호주 우편번호는 주별로 범위가 다름)
        // 첫 번째 검색어가 가장 일반적이므로 우선 시도
        const searchQueries = [
          `${term.trim()}, Australia`, // 가장 일반적인 검색어
          `${term.trim()}, NSW, Australia`,
          `${term.trim()}, VIC, Australia`,
          `${term.trim()}, QLD, Australia`,
          `${term.trim()}, SA, Australia`,
          `${term.trim()}, WA, Australia`,
        ];

        // 첫 번째 검색어로 시도하고, 결과가 없으면 다음 검색어 시도
        const apiPromise = (async () => {
          for (const searchQuery of searchQueries) {
            try {
              const res = await fetch(
                `/api/geocode?address=${encodeURIComponent(
                  searchQuery
                )}&region=au`
              );

              if (!res.ok) {
                console.warn(
                  `Geocoding API error for "${searchQuery}": ${res.status}`
                );
                continue;
              }

              const data = await res.json();

              // 상세 로깅
              console.log(`📍 Geocoding response for "${searchQuery}":`, {
                status: data.status,
                error_message: data.error_message,
                results_count: data.results?.length || 0,
                full_response: data,
              });

              // 에러 메시지가 있으면 로깅
              if (data.error_message) {
                console.error(
                  `❌ Geocoding API error for "${searchQuery}":`,
                  data.error_message
                );
              }

              // 상태가 OK이고 결과가 있을 때만 처리
              if (
                data.status === "OK" &&
                data.results &&
                data.results.length > 0
              ) {
                // 우편번호가 일치하는 결과 필터링 (정확히 일치하거나 포함)
                const matchingResults = data.results.filter((result: any) => {
                  const postcodeComponent = result.address_components?.find(
                    (c: any) => c.types.includes("postal_code")
                  );
                  const postcode = postcodeComponent?.long_name;
                  // 정확히 일치하거나, 우편번호가 결과에 포함되어 있으면 OK
                  return postcode === term.trim() || 
                         (postcode && postcode.includes(term.trim()));
                });

                console.log(
                  `📍 Matching results for postcode ${term.trim()}:`,
                  matchingResults.length
                );

                if (matchingResults.length > 0) {
                  // Geocoding 결과를 Places Autocomplete 형식으로 변환
                  const predictions = matchingResults
                    .slice(0, 10)
                    .map((result: any) => {
                      const postcode =
                        result.address_components?.find((c: any) =>
                          c.types.includes("postal_code")
                        )?.long_name || term.trim();
                      const city =
                        result.address_components?.find(
                          (c: any) =>
                            c.types.includes("locality") ||
                            c.types.includes("sublocality")
                        )?.long_name || "";
                      const state =
                        result.address_components?.find((c: any) =>
                          c.types.includes("administrative_area_level_1")
                        )?.short_name || "";

                      return {
                        place_id: result.place_id,
                        description: result.formatted_address,
                        structured_formatting: {
                          main_text: postcode,
                          secondary_text:
                            [city, state].filter(Boolean).join(", ") ||
                            "Australia",
                        },
                        geocodeResult: {
                          lat: result.geometry.location.lat,
                          lng: result.geometry.location.lng,
                          city,
                          state,
                          postcode,
                          formattedAddress: result.formatted_address,
                        } as GeocodeResult,
                      };
                    });

                  return predictions;
                } else {
                  console.warn(
                    `📍 No matching postcode in results for "${searchQuery}"`
                  );
                }
              } else if (data.status === "ZERO_RESULTS") {
                console.log(`📍 Zero results for "${searchQuery}"`);
              } else if (data.status === "REQUEST_DENIED") {
                // REQUEST_DENIED는 서버 설정 문제이므로 첫 번째 시도에서만 로깅
                if (searchQuery === searchQueries[0]) {
                  console.error(
                    `❌ REQUEST_DENIED: API key configuration issue. ` +
                      `Please set GCP_MAPS_BACKEND_KEY in Cloudflare Pages environment variables ` +
                      `with Application restriction: None. ` +
                      `See docs/GEOCODING_API_SETUP.md for setup instructions.`
                  );
                }
                // 다른 검색어 시도 계속
                continue;
              } else if (data.status !== "OK") {
                console.error(
                  `❌ Geocoding API returned non-OK status: ${data.status}`,
                  data.error_message
                );
              }
            } catch (error) {
              console.warn(`Error searching with "${searchQuery}":`, error);
              continue;
            }
          }
          return [];
        })();

        try {
          const predictions = (await Promise.race([
            apiPromise,
            timeoutPromise,
          ])) as any[];
          console.log("📍 Postcode Geocoding response:", predictions);

          if (predictions && predictions.length > 0) {
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            console.warn("📍 No results for postcode:", term.trim());
            setSuggestions([]);
            setShowSuggestions(false);
          }
        } catch (error: any) {
          if (error.message === "Timeout") {
            console.warn("⏱️ Geocoding API timeout for postcode search");
            setSuggestions([]);
            setShowSuggestions(false);
          } else {
            console.error("❌ Postcode search error:", error);
            setSuggestions([]);
            setShowSuggestions(false);
          }
        }
        return;
      }

      // 지역명 검색 - 로컬 검색 결과가 있으면 즉시 표시하고, Google Places는 백그라운드에서 처리
      // 타임아웃 설정 (3초로 단축)
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), 3000)
      );

      const apiPromise = getJson(
        `/api/places/autocomplete?input=${encodeURIComponent(
          term
        )}&types=(regions)&components=country:au`,
        {},
        PlacesAutocompleteSchema
      );

      // Google Places API 호출 (타임아웃 적용)
      Promise.race([apiPromise, timeoutPromise])
        .then((data: any) => {
          if (
            data &&
            data.status === "OK" &&
            data.predictions &&
            data.predictions.length > 0
          ) {
            // 로컬 검색 결과가 없거나 Google Places 결과가 더 나으면 표시
            setSuggestions(data.predictions);
            setShowSuggestions(true);
          }
        })
        .catch((error: any) => {
          if (error.message !== "Timeout") {
            console.error("Google Places API error:", error);
          }
          // 타임아웃이면 로컬 검색 결과만 사용 (이미 표시됨)
        });
    } catch (error) {
      console.error("Autocomplete error:", error);
    }
  }, []);

  // 지역 검색 (우편번호 또는 지역명) - 로컬 검색
  const searchLocations = useMemo(() => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return [];
    }

    const term = searchTerm.trim().toLowerCase();
    const results: Array<{
      state: StateCode;
      suburb?: string;
      name: string;
      nameEn: string;
      matchType: "postcode" | "name" | "nameKo";
    }> = [];

    // 우편번호 검색 (숫자만 입력한 경우) - 로컬 데이터에는 우편번호가 없으므로 빈 배열 반환
    if (/^\d+$/.test(term)) {
      return results;
    }

    // 지역명 검색 (영어/한글)
    Object.entries(suburbsByState).forEach(([stateId, suburbs]) => {
      if (stateId !== "ALL") {
        (suburbs as SuburbInfo[]).forEach((suburb: SuburbInfo) => {
          const nameMatch = suburb.name.toLowerCase().includes(term);
          const nameKoMatch = suburb.nameKo?.toLowerCase().includes(term);

          if (nameMatch || nameKoMatch) {
            results.push({
              state: stateId as StateCode,
              suburb: suburb.id,
              name: suburb.nameKo || suburb.name,
              nameEn: suburb.name,
              matchType: nameMatch ? "name" : "nameKo",
            });
          }
        });
      }
    });

    return results.slice(0, 20); // 최대 20개만 표시
  }, [searchTerm]);

  // 입력값 변경 시 자동완성 검색 (debounced) - 로컬 검색 즉시 표시
  useEffect(() => {
    const term = searchTerm.trim();
    const isPostcode = /^\d+$/.test(term);

    // 우편번호의 경우 최소 4자리부터 검색 시작 (호주 우편번호는 4자리)
    // 지역명의 경우 최소 2자부터 로컬 검색 결과 표시
    if (isPostcode) {
      // 우편번호는 4자리 이상일 때만 Geocoding API 사용
      if (term.length >= 4) {
        const timer = setTimeout(() => {
          handleAutocompleteSearch(term);
        }, 300); // 300ms debounce
        return () => clearTimeout(timer);
      } else {
        // 4자리 미만이면 검색하지 않음
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
    } else {
      // 지역명 검색
      // 로컬 검색 결과가 있으면 즉시 표시
      if (term.length >= 2 && searchLocations.length > 0) {
        setShowSuggestions(true);
      } else if (term.length < 1) {
        setShowSuggestions(false);
      }

      // Google Places API는 debounce 후 호출
      const timer = setTimeout(() => {
        if (term.length >= 1) {
          handleAutocompleteSearch(term);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    }
  }, [searchTerm, handleAutocompleteSearch, searchLocations]);

  // 자동완성 결과 선택
  const handleSuggestionSelect = async (
    placeId: string,
    description: string,
    geocodeResult?: GeocodeResult
  ) => {
    try {
      // Geocoding 결과가 있으면 직접 사용 (우편번호 검색의 경우)
      if (geocodeResult) {
        const detectedState =
          (geocodeResult.state as StateCode) ||
          (getStateFromCoordinates(
            geocodeResult.lat,
            geocodeResult.lng
          ) as StateCode);
        setTempState(detectedState);

        // 가장 가까운 동네 찾기
        const nearestSuburb = getNearestSuburb(
          geocodeResult.lat,
          geocodeResult.lng,
          detectedState
        );
        if (nearestSuburb) {
          setTempSuburb(nearestSuburb.id);
        } else if (geocodeResult.city) {
          setTempSuburb(geocodeResult.city);
        } else if (geocodeResult.postcode) {
          setTempSuburb(geocodeResult.postcode);
        }

        setSearchTerm(description);
        setSuggestions([]);
        setShowSuggestions(false);

        toast({
          title: "지역이 선택되었습니다",
          description: `${description}`,
        });
        return;
      }

      // Places API 결과인 경우 기존 로직 사용
      const data = await getJson(
        `/api/places/details?placeId=${encodeURIComponent(placeId)}`,
        {},
        PlacesDetailsSchema
      );

      if (data.result && data.result.geometry) {
        const lat = data.result.geometry.location.lat;
        const lng = data.result.geometry.location.lng;
        const detectedState = getStateFromCoordinates(lat, lng) as StateCode;
        setTempState(detectedState);

        // 주소 컴포넌트에서 정보 추출
        const components = data.result.address_components || [];
        let city = "";
        let postcode = "";

        components.forEach((component: any) => {
          const types = component.types;
          if (
            types.includes("locality") ||
            types.includes("sublocality") ||
            types.includes("postal_town")
          ) {
            city = component.long_name;
          }
          if (types.includes("postal_code")) {
            postcode = component.long_name;
          }
        });

        // 가장 가까운 동네 찾기
        const nearestSuburb = getNearestSuburb(lat, lng, detectedState);
        if (nearestSuburb) {
          setTempSuburb(nearestSuburb.id);
        } else if (city) {
          setTempSuburb(city);
        } else {
          setTempSuburb(postcode || description);
        }

        setSearchTerm(description);
        setSuggestions([]);
        setShowSuggestions(false);

        toast({
          title: "지역이 선택되었습니다",
          description: `${description}`,
        });
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
      toast({
        title: "오류",
        description: "지역 정보를 가져오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    }
  };

  // 주요도시 목록 (5개만 한 줄로 표시)
  const majorCities = [
    {
      state: "NSW" as StateCode,
      suburb: "sydney",
      name: "시드니",
      nameEn: "Sydney",
    },
    {
      state: "VIC" as StateCode,
      suburb: "melbourne",
      name: "멜버른",
      nameEn: "Melbourne",
    },
    {
      state: "QLD" as StateCode,
      suburb: "brisbane",
      name: "브리즈번",
      nameEn: "Brisbane",
    },
    {
      state: "WA" as StateCode,
      suburb: "perth",
      name: "퍼스",
      nameEn: "Perth",
    },
    {
      state: "SA" as StateCode,
      suburb: "adelaide",
      name: "애들레이드",
      nameEn: "Adelaide",
    },
  ];

  // Note: Radix Dialog already handles portal and aria-hidden automatically
  // We don't need to manually set inert/aria-hidden on #root

  // 추천 지역 목록 (주별로 그룹화)
  const recommendedLocationsByState = useMemo(() => {
    const grouped: Record<
      string,
      Array<{ state: StateCode; suburb?: string; name: string; nameEn: string }>
    > = {};

    // 모든 주의 주요 지역을 주별로 그룹화
    Object.entries(suburbsByState).forEach(([stateId, suburbs]) => {
      if (stateId !== "ALL") {
        grouped[stateId] = (suburbs as SuburbInfo[]).map(
          (suburb: SuburbInfo) => ({
            state: stateId as StateCode,
            suburb: suburb.id,
            name: suburb.nameKo || suburb.name,
            nameEn: suburb.name,
          })
        );
      }
    });

    return grouped;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px] bg-black border-[#333333] text-white p-0 [&>button]:hidden"
        data-testid="dialog-location-selector"
        onOpenAutoFocus={(e) => {
          const firstInput = searchInputRef.current;
          if (firstInput) {
            e.preventDefault();
            requestAnimationFrame(() => {
              firstInput.focus();
            });
          }
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>지역 변경</DialogTitle>
          <DialogDescription>
            지역을 검색하거나 선택하여 설정하세요
          </DialogDescription>
        </DialogHeader>

        {/* Header - 지역 변경 제목과 X 버튼 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#333333]">
          <h2 className="text-lg font-semibold text-white">지역 변경</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="text-white hover:text-[#60a6fa] transition-colors"
            data-testid="button-close-location-dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* 검색 입력 필드 */}
          <div className="relative">
            <Input
              ref={searchInputRef}
              type="text"
              placeholder="지역이나 동네로 검색하기"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-12 px-4 pr-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#999999] focus-visible:ring-1 focus-visible:ring-[#60a6fa]"
              autoComplete="off"
              data-testid="input-location-search"
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchTerm.trim().length >= 2) {
                  onSearch();
                }
                if (e.key === "Escape") {
                  setShowSuggestions(false);
                }
              }}
              onFocus={() => {
                if (suggestions.length > 0 || searchLocations.length > 0) {
                  setShowSuggestions(true);
                }
              }}
            />
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#999999]" />

            {/* 자동완성 드롭다운 (Google Places) */}
            {showSuggestions && suggestions.length > 0 && (
              <div
                className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#333333] rounded-lg shadow-lg max-h-60 overflow-auto"
                role="listbox"
              >
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.place_id}
                    className="px-4 py-3 hover:bg-[#2a2a2a] cursor-pointer border-b border-[#333333] last:border-b-0"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSuggestionSelect(
                        suggestion.place_id,
                        suggestion.description,
                        (suggestion as any).geocodeResult
                      );
                    }}
                    role="option"
                    data-testid={`suggestion-${suggestion.place_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-[#60a6fa] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white">
                          {suggestion.description}
                        </div>
                        {suggestion.structured_formatting?.secondary_text && (
                          <div className="text-xs text-[#999999] mt-0.5">
                            {suggestion.structured_formatting.secondary_text}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 로컬 검색 결과 (우편번호/지역명) */}
            {showSuggestions &&
              searchLocations.length > 0 &&
              suggestions.length === 0 && (
                <div
                  className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#333333] rounded-lg shadow-lg max-h-60 overflow-auto"
                  role="listbox"
                >
                  {searchLocations.map((location, index) => (
                    <div
                      key={`${location.state}-${
                        location.suburb || "state"
                      }-${index}`}
                      className="px-4 py-3 hover:bg-[#2a2a2a] cursor-pointer border-b border-[#333333] last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onMajorCitySelect(location.state, location.suburb);
                        setSearchTerm(location.name);
                        setShowSuggestions(false);
                      }}
                      role="option"
                      data-testid={`search-result-${location.state}-${location.suburb}`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-[#60a6fa] mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white">
                            {location.name}
                          </div>
                          <div className="text-xs text-[#999999] mt-0.5">
                            {australianStates.find(
                              (s) => s.id === location.state
                            )?.nameKo || location.state}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>

          {/* 현재 내 위치 사용하기 버튼 */}
          <button
            onClick={onDetect}
            disabled={detecting}
            className="w-full px-4 py-3 rounded-lg bg-[#1a1a1a] border border-[#333333] text-[#FF7A00] hover:bg-[#2a2a2a] transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="button-detect-location"
          >
            <MapPin className="h-4 w-4" />
            <span className="text-sm font-medium">
              {detecting ? "위치 감지 중..." : "현재 내 위치 사용하기"}
            </span>
          </button>

          {/* 추천 지역 목록 (주별로 그룹화) - 텍스트 링크 형태 */}
          <div className="space-y-2.5">
            <h3 className="text-xs font-semibold text-[#60a6fa]">추천</h3>
            <div className="max-h-[300px] overflow-y-auto space-y-2.5">
              {Object.entries(recommendedLocationsByState).map(
                ([stateId, locations]) => {
                  const stateInfo = australianStates.find(
                    (s) => s.id === stateId
                  );
                  if (!stateInfo || locations.length === 0) return null;

                  return (
                    <div key={stateId} className="space-y-1">
                      <h4 className="text-xs font-medium text-white">
                        {stateInfo.nameKo}
                      </h4>
                      <div className="flex flex-wrap gap-x-2.5 gap-y-0.5">
                        {locations.map((location, idx) => {
                          const isSelected =
                            tempState === location.state &&
                            tempSuburb === location.suburb;
                          return (
                            <span
                              key={`${location.state}-${
                                location.suburb || "state"
                              }`}
                            >
                              <button
                                onClick={() => {
                                  onMajorCitySelect(
                                    location.state,
                                    location.suburb
                                  );
                                }}
                                className={`text-xs transition-colors hover:text-[#60a6fa] ${
                                  isSelected
                                    ? "text-[#60a6fa] font-medium"
                                    : "text-[#999999]"
                                }`}
                                data-testid={`recommended-location-${location.state}-${location.suburb}`}
                              >
                                {location.name}
                              </button>
                              {idx < locations.length - 1 && (
                                <span className="text-[#333333] mx-0.5 text-xs">
                                  ·
                                </span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>

        {/* Footer - 저장 버튼 */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#333333]">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-[#1a1a1a] border-[#333333] text-white hover:bg-[#2a2a2a]"
          >
            취소
          </Button>
          <Button
            type="button"
            onClick={onSave}
            className="bg-[#60a6fa] hover:bg-[#4d96f0] text-white"
          >
            저장
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
